/**
 * Movement System - handles unit movement with A* pathfinding
 * Applies terrain speed modifiers and follows calculated paths
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { MovementComponent } from '@/game/components/Movement';
import type { FactionComponent } from '@/game/components/Faction';
import type { UnitComponent } from '@/game/components/Unit';
import { getTerrainSpeedModifier, getSurfaceY, tileHeight, isLand } from '@/rendering/terrain/TerrainGenerator';
import { findPath, hasLineOfSight } from '@/core/utils/Pathfinding';

// How often to check if stuck (seconds)
const STUCK_CHECK_INTERVAL = 0.5;
// Minimum movement to not be considered stuck
const STUCK_THRESHOLD = 0.1;
// How long to be stuck before recalculating path
const STUCK_RECALC_TIME = 1.0;
// Minimum time between path recalculations
const PATH_RECALC_COOLDOWN = 2.0;
// Distance to waypoint to consider it reached
const WAYPOINT_REACH_DIST = 0.5;
// Distance to final target to consider arrived
const ARRIVAL_DIST = 0.3;

export class MovementSystem implements System {
  update(world: World, deltaTime: number): void {
    const entities = world.query([ComponentTypes.Position, ComponentTypes.Movement]);

    for (const entity of entities) {
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const movement = world.getComponent<MovementComponent>(entity, ComponentTypes.Movement)!;

      // Get faction and unit type for terrain bonus check
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);
      const unit = world.getComponent<UnitComponent>(entity, ComponentTypes.Unit);
      const factionId = faction?.factionId;
      const unitType = unit?.unitType;

      // Calculate terrain speed modifier (now includes unit type)
      const terrainModifier = getTerrainSpeedModifier(position.x, position.z, factionId, unitType);

      // If no target, apply any existing velocity with decay
      if (movement.finalTargetX === null || movement.finalTargetZ === null) {
        movement.velocityX *= 0.9;
        movement.velocityZ *= 0.9;

        if (Math.abs(movement.velocityX) > 0.01 || Math.abs(movement.velocityZ) > 0.01) {
          const newX = position.x + movement.velocityX * deltaTime * terrainModifier;
          const newZ = position.z + movement.velocityZ * deltaTime * terrainModifier;

          // Only apply momentum if new position is on land
          if (isLand(tileHeight(newX, newZ))) {
            position.x = newX;
            position.z = newZ;
            position.y = getSurfaceY(newX, newZ);
          } else {
            // Stop momentum if would enter water
            movement.velocityX = 0;
            movement.velocityZ = 0;
          }
        }
        continue;
      }

      // Update stuck detection timer
      movement.stuckTimer += deltaTime;
      if (movement.stuckTimer >= STUCK_CHECK_INTERVAL) {
        const movedDist = Math.sqrt(
          (position.x - movement.lastX) ** 2 +
          (position.z - movement.lastZ) ** 2
        );

        if (movedDist < STUCK_THRESHOLD) {
          // Still stuck, accumulate time
          movement.pathRecalcTimer += movement.stuckTimer;
        } else {
          // Moving fine, reset
          movement.pathRecalcTimer = 0;
        }

        movement.lastX = position.x;
        movement.lastZ = position.z;
        movement.stuckTimer = 0;
      }

      // Check if we need to calculate/recalculate path
      if (movement.path === null || movement.pathRecalcTimer >= STUCK_RECALC_TIME) {
        this.calculatePath(position, movement);
        movement.pathRecalcTimer = 0;
      }

      // If still no path, try direct movement as fallback
      if (movement.path === null || movement.path.length === 0) {
        this.moveDirectly(position, movement, deltaTime, terrainModifier);
        continue;
      }

      // Follow the path
      this.followPath(position, movement, deltaTime, terrainModifier);
    }
  }

  private calculatePath(position: PositionComponent, movement: MovementComponent): void {
    if (movement.finalTargetX === null || movement.finalTargetZ === null) {
      movement.path = null;
      return;
    }

    // Check for line of sight first - if clear, go direct
    if (hasLineOfSight(position.x, position.z, movement.finalTargetX, movement.finalTargetZ)) {
      movement.path = [{ x: movement.finalTargetX, z: movement.finalTargetZ }];
      movement.currentWaypointIndex = 0;
      return;
    }

    // Calculate A* path
    const path = findPath(
      position.x,
      position.z,
      movement.finalTargetX,
      movement.finalTargetZ
    );

    if (path && path.length > 0) {
      movement.path = path;
      movement.currentWaypointIndex = 0;
    } else {
      // No path found - stop movement, don't try to go through obstacles
      this.clearMovement(movement);
    }
  }

  private followPath(
    position: PositionComponent,
    movement: MovementComponent,
    deltaTime: number,
    terrainModifier: number
  ): void {
    const path = movement.path!;

    // Advance waypoint index if current waypoint reached
    while (movement.currentWaypointIndex < path.length) {
      const waypoint = path[movement.currentWaypointIndex];
      const dx = waypoint.x - position.x;
      const dz = waypoint.z - position.z;
      const distSq = dx * dx + dz * dz;

      // Check if this is the final waypoint
      const isFinal = movement.currentWaypointIndex === path.length - 1;
      const reachDist = isFinal ? ARRIVAL_DIST : WAYPOINT_REACH_DIST;

      if (distSq < reachDist * reachDist) {
        movement.currentWaypointIndex++;
        if (movement.currentWaypointIndex >= path.length) {
          // Arrived at destination
          this.clearMovement(movement);
          return;
        }
      } else {
        break;
      }
    }

    // Move toward current waypoint
    if (movement.currentWaypointIndex < path.length) {
      const waypoint = path[movement.currentWaypointIndex];

      // Also set the immediate target for other systems (like combat) to reference
      movement.targetX = waypoint.x;
      movement.targetZ = waypoint.z;

      this.moveToward(position, movement, waypoint.x, waypoint.z, deltaTime, terrainModifier);
    }
  }

  private moveDirectly(
    position: PositionComponent,
    movement: MovementComponent,
    deltaTime: number,
    terrainModifier: number
  ): void {
    if (movement.finalTargetX === null || movement.finalTargetZ === null) return;

    movement.targetX = movement.finalTargetX;
    movement.targetZ = movement.finalTargetZ;

    const dx = movement.finalTargetX - position.x;
    const dz = movement.finalTargetZ - position.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist < ARRIVAL_DIST) {
      this.clearMovement(movement);
      return;
    }

    this.moveToward(position, movement, movement.finalTargetX, movement.finalTargetZ, deltaTime, terrainModifier);
  }

  private moveToward(
    position: PositionComponent,
    movement: MovementComponent,
    targetX: number,
    targetZ: number,
    deltaTime: number,
    terrainModifier: number
  ): void {
    const dx = targetX - position.x;
    const dz = targetZ - position.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist < 0.01) return;

    // Apply terrain modifier to effective speed
    const effectiveSpeed = movement.speed * terrainModifier;

    // Calculate next position
    const actualMove = Math.min(dist, effectiveSpeed * deltaTime);
    const nextX = position.x + (dx / dist) * actualMove;
    const nextZ = position.z + (dz / dist) * actualMove;

    // Check if next position is walkable (not water or obstacle)
    const nextHeight = tileHeight(nextX, nextZ);
    if (!isLand(nextHeight)) {
      // Would move into water - stop and recalculate path
      movement.path = null;
      movement.pathRecalcTimer = PATH_RECALC_COOLDOWN; // Force recalc
      return;
    }

    // Normalize direction and apply speed
    const moveX = (dx / dist) * effectiveSpeed;
    const moveZ = (dz / dist) * effectiveSpeed;

    // Update velocity
    movement.velocityX = moveX;
    movement.velocityZ = moveZ;

    // Move toward target
    position.x = nextX;
    position.z = nextZ;

    // Update Y position based on terrain height
    position.y = getSurfaceY(position.x, position.z);
  }

  private clearMovement(movement: MovementComponent): void {
    movement.targetX = null;
    movement.targetZ = null;
    movement.finalTargetX = null;
    movement.finalTargetZ = null;
    movement.velocityX = 0;
    movement.velocityZ = 0;
    movement.path = null;
    movement.currentWaypointIndex = 0;
  }
}

/**
 * Helper function to set a unit's movement target with pathfinding
 */
export function setMovementTarget(
  movement: MovementComponent,
  targetX: number,
  targetZ: number,
  isPlayerCommand: boolean = false
): void {
  movement.finalTargetX = targetX;
  movement.finalTargetZ = targetZ;
  movement.targetX = targetX;
  movement.targetZ = targetZ;
  movement.isPlayerCommand = isPlayerCommand;
  movement.path = null; // Force path recalculation
  movement.currentWaypointIndex = 0;
  movement.pathRecalcTimer = 0;
  movement.stuckTimer = 0;
}

/**
 * Helper function to save current movement for later restoration
 */
export function saveMovement(movement: MovementComponent): void {
  movement.savedTargetX = movement.finalTargetX;
  movement.savedTargetZ = movement.finalTargetZ;
  movement.savedIsPlayerCommand = movement.isPlayerCommand;
}

/**
 * Helper function to restore saved movement
 */
export function restoreMovement(movement: MovementComponent): void {
  if (movement.savedTargetX !== null && movement.savedTargetZ !== null) {
    setMovementTarget(
      movement,
      movement.savedTargetX,
      movement.savedTargetZ,
      movement.savedIsPlayerCommand
    );
  }
  movement.savedTargetX = null;
  movement.savedTargetZ = null;
  movement.savedIsPlayerCommand = false;
}
